import {
  ArrowLeft,
  ArrowRight,
  Bath,
  Calculator,
  Camera,
  Check,
  CheckCircle,
  ListTree,
  Minus,
  Package,
  PanelsTopLeft,
  Plus,
  Ruler,
  Shapes,
  Sparkles,
  ToggleLeft,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalculationInput } from "@/components/CalculationInput";
import PhotoUpload from "@/components/PhotoUpload";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useComponentValidation } from "@/hooks/checklist/useComponentValidation";
import { useComponentVisibility } from "@/hooks/checklist/useComponentVisibility";
import { useToast } from "@/hooks/use-toast";
import type {
  Checklist,
  ChecklistComponent,
  ChecklistResponseEntry,
  ChecklistResponseValue,
  ChecklistScalarValue,
  LegacyChecklist,
} from "@/types/quote.types";
import {
  clampQuantity as clampQuantityUtil,
  componentRequiresQuantity as componentRequiresQuantityUtil,
  extractScalarValue,
  flattenComponents as flattenComponentsUtil,
  isResponseEntry,
  resolveOptionValue as resolveOptionValueUtil,
} from "@/utils/quoteHelpers";
import { type ValidationResult, validateChecklistComponents } from "@/utils/validation";

type QuantityMap = Record<string, number> | Record<number, Record<string, number>>;

const isFlatQuantityMap = (map: QuantityMap): map is Record<string, number> => {
  return Object.values(map).every((value) => typeof value === "number");
};

interface LegacyQuoteChecklistProps {
  checklist: LegacyChecklist;
  isCompleted: boolean;
  onComplete: () => void;
  onDelete: () => void;
}

interface DynamicQuoteChecklistProps {
  checklist: Checklist;
  responses: Record<string, ChecklistResponseValue>;
  onResponseChange: (fieldName: string, value: ChecklistResponseValue) => void;
  onQuantityChange?: (checklistId: number, fieldName: string, quantity?: number) => void;
  quantities?: QuantityMap;
  onChecklistComplete?: (checklistId: number) => void;
  onChecklistUnconfirm?: (checklistId: number) => void;
  onMoveToNextChecklist?: () => void;
  onCancel?: () => void;
  isCompleted?: boolean;
}

type QuoteChecklistProps = LegacyQuoteChecklistProps | DynamicQuoteChecklistProps;

function isDynamicChecklist(props: QuoteChecklistProps): props is DynamicQuoteChecklistProps {
  return "responses" in props && "onResponseChange" in props;
}

const QuoteChecklist = (props: QuoteChecklistProps) => {
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [measurements, setMeasurements] = useState({ width: "", height: "" });
  const [powerPoints, setPowerPoints] = useState(1);
  const [electricalOptions, setElectricalOptions] = useState({
    xl: false,
    downlights: false,
    heatedTowelRail: false,
    ledCabinet: false,
  });
  const [vanityType, setVanityType] = useState("");
  const [vanitySize, setVanitySize] = useState("");
  const [customNotes, setCustomNotes] = useState("");
  const [customLines, setCustomLines] = useState([""]);
  const [optionalNotes, setOptionalNotes] = useState("");
  const [componentValues, setComponentValues] = useState<Record<string, ChecklistResponseValue>>(
    {}
  );
  const [validationErrors, setValidationErrors] = useState<Record<string, ValidationResult>>({});

  // Handle dynamic database-driven checklists
  if (isDynamicChecklist(props)) {
    const {
      checklist,
      responses,
      onResponseChange,
      onQuantityChange,
      quantities,
      onChecklistComplete,
      onChecklistUnconfirm,
      onMoveToNextChecklist,
      onCancel,
      isCompleted,
    } = props;
    const { toast } = useToast();

    const toResponseEntry = (
      value: ChecklistResponseValue | undefined
    ): ChecklistResponseEntry | undefined => {
      if (value === undefined || value === null) {
        return undefined;
      }

      if (isResponseEntry(value)) {
        return value;
      }

      return { value: value as ChecklistScalarValue };
    };

    const resolveScalarResponses = useMemo(() => {
      const resolved: Record<string, ChecklistScalarValue> = {};

      Object.entries(responses).forEach(([fieldName, rawValue]) => {
        const scalarValue = extractScalarValue(rawValue);
        if (scalarValue !== undefined && scalarValue !== null) {
          resolved[fieldName] = scalarValue;
        }
      });

      return resolved;
    }, [responses]);

    const flattenComponents = useMemo(() => {
      return flattenComponentsUtil(checklist.components || []);
    }, [checklist.components]);

    const { isComponentVisible } = useComponentVisibility(resolveScalarResponses);

    const visibleComponents = useMemo(() => {
      return flattenComponents.filter((component) => isComponentVisible(component));
    }, [flattenComponents, isComponentVisible]);

    const { validationResults, validationSummary, hasValue } = useComponentValidation(
      visibleComponents,
      resolveScalarResponses
    );

    type ComponentType =
      | "simple"
      | "dropdown"
      | "yesno"
      | "complex"
      | "measurements"
      | "calculation"
      | "electrical"
      | "vanity"
      | "supply"
      | "screen"
      | "custom";

    const getComponentType = useCallback((component: ChecklistComponent): ComponentType => {
      // Check for calculation component first
      if (component.calculation_formula || component.calculation_inputs) {
        return "calculation";
      }

      const label = component.label?.toLowerCase() ?? "";
      if (label.includes("electrical")) {
        return "electrical";
      }
      if (label.includes("vanity")) {
        return "vanity";
      }
      if (label.includes("supply")) {
        return "supply";
      }
      if (label.includes("shower") && label.includes("screen")) {
        return "screen";
      }

      if (
        component.allow_quantity_input ||
        ["per_unit", "per_sqft", "per_linear"].includes(component.pricing_type || "")
      ) {
        return "measurements";
      }

      if (component.component_type === "select" || component.component_type === "radio") {
        return "dropdown";
      }

      if (component.component_type === "checkbox") {
        return component.options && component.options.length <= 2 ? "yesno" : "complex";
      }

      if (component.component_type === "number") {
        return "measurements";
      }

      if (component.children && component.children.length > 0) {
        return "complex";
      }

      if (component.help_text || component.placeholder) {
        return "custom";
      }

      return "simple";
    }, []);

    const componentTypeLabel: Record<ComponentType, string> = {
      simple: "Item",
      dropdown: "Selection",
      yesno: "Yes/No",
      complex: "Complex",
      measurements: "Measurements",
      calculation: "Calculation",
      electrical: "Electrical",
      vanity: "Vanity",
      supply: "Supply",
      screen: "Shower Screen",
      custom: "Custom",
    };

    const componentTypeIcon: Record<ComponentType, typeof CheckCircle> = {
      simple: CheckCircle,
      dropdown: ListTree,
      yesno: ToggleLeft,
      complex: Shapes,
      measurements: Ruler,
      calculation: Calculator,
      electrical: Zap,
      vanity: Bath,
      supply: Package,
      screen: PanelsTopLeft,
      custom: Sparkles,
    };

    const rootComponents = useMemo(() => {
      return (checklist.components || []).filter((component) => !component.parent_component_id);
    }, [checklist.components]);

    const visibleRootComponents = useMemo(() => {
      return rootComponents.filter((component) => isComponentVisible(component));
    }, [isComponentVisible, rootComponents]);

    const isComponentCompleted = useCallback(
      (component: ChecklistComponent): boolean => {
        if (!isComponentVisible(component)) {
          return true;
        }

        const validation = validationResults[component.field_name];
        const valueSatisfied = hasValue(component);
        const isValid = validation ? validation.isValid !== false : true;
        const requiredSatisfied = component.is_required ? valueSatisfied && isValid : true;

        if (!requiredSatisfied) {
          return false;
        }

        if (component.children && component.children.length > 0) {
          return component.children.every((child) => isComponentCompleted(child));
        }

        return true;
      },
      [hasValue, isComponentVisible, validationResults]
    );

    const completedItemCount = useMemo(() => {
      return visibleRootComponents.filter((component) => isComponentCompleted(component)).length;
    }, [isComponentCompleted, visibleRootComponents]);

    const totalItemCount = visibleRootComponents.length || 1;
    const completionPercentage = Math.round((completedItemCount / totalItemCount) * 100);

    const [activeItemId, setActiveItemId] = useState<number | null>(null);
    const [showDetails, setShowDetails] = useState<Record<number, boolean>>({});
    const [itemNotes, setItemNotes] = useState<Record<number, string>>({});
    const [itemImages, setItemImages] = useState<Record<number, string[]>>({});
    const hasAutoOpened = useRef(false);

    useEffect(() => {
      if (activeItemId === null) {
        return;
      }
      const stillVisible = visibleRootComponents.some((component) => component.id === activeItemId);
      if (!stillVisible) {
        setActiveItemId(null);
      }
    }, [activeItemId, visibleRootComponents]);

    // Initialize notes and images from existing responses
    useEffect(() => {
      const notes: Record<number, string> = {};
      const images: Record<number, string[]> = {};

      flattenComponents.forEach((component) => {
        const response = responses[component.field_name];
        const entry = toResponseEntry(response);

        if (entry?.notes) {
          notes[component.id] = entry.notes;
        }
        if (entry?.images && entry.images.length > 0) {
          images[component.id] = entry.images;
        }
      });

      setItemNotes(notes);
      setItemImages(images);
    }, [responses, flattenComponents]);

    // Auto-expand details section when item has existing notes or images
    useEffect(() => {
      if (activeItemId === null) {
        return;
      }
      const hasNotes = itemNotes[activeItemId] && itemNotes[activeItemId].trim().length > 0;
      const hasImages = itemImages[activeItemId] && itemImages[activeItemId].length > 0;
      if (hasNotes || hasImages) {
        setShowDetails((prev) => ({ ...prev, [activeItemId]: true }));
      }
    }, [activeItemId, itemNotes, itemImages]);

    // Auto-open first component when checklist is first loaded (only once)
    useEffect(() => {
      if (!hasAutoOpened.current && activeItemId === null && visibleRootComponents.length > 0) {
        // Find first incomplete component, or just use the first one
        const firstIncomplete = visibleRootComponents.find(
          (component) => !isComponentCompleted(component)
        );
        const firstComponent = firstIncomplete || visibleRootComponents[0];
        setActiveItemId(firstComponent?.id ?? null);
        hasAutoOpened.current = true;
      }
    }, [visibleRootComponents, isComponentCompleted, activeItemId]);

    const activeItem = useMemo(() => {
      if (activeItemId === null) {
        return undefined;
      }

      return flattenComponents.find((component) => component.id === activeItemId);
    }, [activeItemId, flattenComponents]);

    const goToNextItem = useCallback(() => {
      if (!visibleRootComponents.length) {
        setActiveItemId(null);
        return;
      }

      if (activeItemId === null) {
        const next = visibleRootComponents.find((component) => !isComponentCompleted(component));
        setActiveItemId(next?.id ?? visibleRootComponents[0]?.id ?? null);
        return;
      }

      const currentIndex = visibleRootComponents.findIndex(
        (component) => component.id === activeItemId
      );
      const remaining = visibleRootComponents.slice(currentIndex + 1);
      const nextIncomplete = remaining.find((component) => !isComponentCompleted(component));
      const fallback = remaining[0];

      if (nextIncomplete) {
        setActiveItemId(nextIncomplete.id ?? null);
        return;
      }

      if (fallback) {
        setActiveItemId(fallback.id ?? null);
        return;
      }

      // No more items - if all items are completed, auto-confirm the checklist and move to next
      const allCompleted = visibleRootComponents.every((component) =>
        isComponentCompleted(component)
      );
      if (allCompleted && validationSummary.isValid) {
        onChecklistComplete?.(checklist.id);
        // Move to next checklist after completing this one
        onMoveToNextChecklist?.();
      }

      setActiveItemId(null);
    }, [
      activeItemId,
      isComponentCompleted,
      visibleRootComponents,
      validationSummary,
      onChecklistComplete,
      onMoveToNextChecklist,
      checklist.id,
    ]);

    const getQuantityForField = (fieldName: string): number | undefined => {
      const rawValue = responses[fieldName];
      if (rawValue && typeof rawValue === "object" && "quantity" in rawValue) {
        return (rawValue as ChecklistResponseEntry).quantity;
      }

      // Check for option-specific quantity (format: fieldName-optionValue)
      const dashIndex = fieldName.lastIndexOf("-");
      if (dashIndex > 0) {
        const parentFieldName = fieldName.substring(0, dashIndex);
        const optionKey = fieldName.substring(dashIndex + 1);
        const parentValue = responses[parentFieldName];
        if (parentValue && typeof parentValue === "object" && "optionQuantities" in parentValue) {
          const optionQuantities = (parentValue as ChecklistResponseEntry).optionQuantities;
          if (optionQuantities && optionKey in optionQuantities) {
            return optionQuantities[optionKey];
          }
        }
      }

      if (!quantities) {
        return undefined;
      }

      if (isFlatQuantityMap(quantities)) {
        return quantities[fieldName];
      }

      const nestedQuantities = quantities as Record<number, Record<string, number>>;
      const perChecklist = nestedQuantities[checklist.id];

      if (perChecklist && typeof perChecklist[fieldName] === "number") {
        return perChecklist[fieldName];
      }

      for (const map of Object.values(nestedQuantities)) {
        if (map && typeof map[fieldName] === "number") {
          return map[fieldName];
        }
      }

      return undefined;
    };

    const emitResponseChange = (
      component: ChecklistComponent,
      value: ChecklistScalarValue,
      quantity?: number
    ) => {
      const entry: ChecklistResponseEntry =
        quantity !== undefined ? { value, quantity } : { value };

      // Add notes and images if they exist for this component
      const notes = itemNotes[component.id];
      const images = itemImages[component.id];

      if (notes) entry.notes = notes;
      if (images && images.length > 0) entry.images = images;

      onResponseChange(component.field_name, entry);

      if (componentRequiresQuantityUtil(component)) {
        onQuantityChange?.(checklist.id, component.field_name, quantity);
      }
    };

    // Save notes and images for a component to its response
    const saveItemDetails = (componentId: number) => {
      const component = flattenComponents.find((c) => c.id === componentId);
      if (!component) return;

      const existingResponse = responses[component.field_name];
      const existingEntry = toResponseEntry(existingResponse);

      if (!existingEntry) return;

      const updatedEntry: ChecklistResponseEntry = { ...existingEntry };

      const notes = itemNotes[componentId];
      const images = itemImages[componentId];

      if (notes) updatedEntry.notes = notes;
      if (images && images.length > 0) updatedEntry.images = images;

      onResponseChange(component.field_name, updatedEntry);
    };

    const handleQuantityChange = (component: ChecklistComponent, rawValue: string) => {
      if (!componentRequiresQuantityUtil(component)) {
        return;
      }

      if (rawValue === "") {
        onQuantityChange?.(checklist.id, component.field_name, undefined);
        const existingValue = resolveScalarResponses[component.field_name];
        if (existingValue !== undefined) {
          onResponseChange(component.field_name, { value: existingValue });
        }
        return;
      }

      const parsed = Number(rawValue);
      if (Number.isNaN(parsed)) {
        return;
      }

      const clamped = clampQuantityUtil(component, parsed);
      const currentValue = resolveScalarResponses[component.field_name];
      if (currentValue !== undefined) {
        emitResponseChange(component, currentValue, clamped);
      } else {
        const defaultValue = (component.default_value || "") as ChecklistScalarValue;
        emitResponseChange(component, defaultValue, clamped);
      }
    };

    const renderComponent = (component: ChecklistComponent, depth = 0): JSX.Element | null => {
      if (!isComponentVisible(component)) {
        return null;
      }

      const scalarValue = resolveScalarResponses[component.field_name];
      const quantityValue = getQuantityForField(component.field_name);
      const validation = validationResults[component.field_name];
      const hasError = validation && !validation.isValid && validation.error;

      const containerClasses = ["space-y-2", depth > 0 ? "pl-4 border-l border-muted" : ""].join(
        " "
      );

      const renderOptions = () => {
        // Check if this is a calculation component (has calculation formula or inputs defined)
        if (component.calculation_formula || component.calculation_inputs) {
          return (
            <CalculationInput
              component={component}
              value={scalarValue}
              onChange={(value) =>
                emitResponseChange(component, value as ChecklistScalarValue, quantityValue)
              }
            />
          );
        }

        switch (component.component_type) {
          case "select":
            return (
              <Select
                value={(scalarValue as string) || ""}
                onValueChange={(value) =>
                  emitResponseChange(component, value as ChecklistScalarValue)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={component.placeholder || `Select ${component.label}`} />
                </SelectTrigger>
                <SelectContent>
                  {component.options?.map((option) => {
                    const optionValue = resolveOptionValueUtil(option);
                    return (
                      <SelectItem key={option.id ?? optionValue} value={optionValue}>
                        {option.option_label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            );
          case "radio":
            return (
              <RadioGroup
                value={(scalarValue as string) || ""}
                onValueChange={(value) =>
                  emitResponseChange(component, value as ChecklistScalarValue)
                }
              >
                {component.options?.map((option) => {
                  const optionValue = resolveOptionValueUtil(option);
                  return (
                    <div key={option.id ?? optionValue} className="flex items-center space-x-2">
                      <RadioGroupItem value={optionValue} />
                      <Label>{option.option_label}</Label>
                    </div>
                  );
                })}
              </RadioGroup>
            );
          case "checkbox": {
            if (component.options && component.options.length > 0) {
              const selectedValues = Array.isArray(scalarValue) ? (scalarValue as string[]) : [];
              const requiresQuantity = componentRequiresQuantityUtil(component);

              // Get existing optionQuantities from the response
              const existingResponse = responses[component.field_name];
              const existingOptionQuantities =
                existingResponse &&
                typeof existingResponse === "object" &&
                "optionQuantities" in existingResponse
                  ? (existingResponse as ChecklistResponseEntry).optionQuantities || {}
                  : {};

              // Helper to emit checkbox response with optionQuantities preserved
              const emitCheckboxResponse = (
                nextValues: string[],
                updatedOptionQuantities?: Record<string, number>
              ) => {
                const entry: ChecklistResponseEntry = { value: nextValues };

                // Merge existing optionQuantities with updates
                const finalOptionQuantities = updatedOptionQuantities || existingOptionQuantities;
                if (Object.keys(finalOptionQuantities).length > 0) {
                  // Only include quantities for selected values
                  const filteredQuantities: Record<string, number> = {};
                  for (const [key, qty] of Object.entries(finalOptionQuantities)) {
                    if (nextValues.includes(key)) {
                      filteredQuantities[key] = qty;
                    }
                  }
                  if (Object.keys(filteredQuantities).length > 0) {
                    entry.optionQuantities = filteredQuantities;
                  }
                }

                // Preserve notes and images
                const notes = itemNotes[component.id];
                const images = itemImages[component.id];
                if (notes) entry.notes = notes;
                if (images && images.length > 0) entry.images = images;

                onResponseChange(component.field_name, entry);
              };

              return (
                <div className="space-y-2">
                  {component.options.map((option) => {
                    const optionValue = resolveOptionValueUtil(option);
                    const isChecked = selectedValues.includes(optionValue);
                    // For multi-select checkboxes, each option needs its own quantity key
                    const optionQuantityKey = `${component.field_name}-${optionValue}`;
                    const optionQuantity =
                      existingOptionQuantities[optionValue] ?? getQuantityForField(optionQuantityKey);

                    return (
                      <div
                        key={option.id ?? optionValue}
                        className="flex items-center justify-between gap-2 rounded border px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => {
                          const nextValues = isChecked
                            ? selectedValues.filter((value) => value !== optionValue)
                            : [...selectedValues, optionValue];
                          emitCheckboxResponse(nextValues);
                        }}
                      >
                        <div className="flex items-center gap-2 flex-1 pointer-events-none">
                          <Checkbox
                            id={`${component.field_name}-${optionValue}`}
                            checked={isChecked}
                            readOnly={true}
                          />
                          <Label
                            htmlFor={`${component.field_name}-${optionValue}`}
                            className="font-normal"
                          >
                            {option.option_label}
                          </Label>
                        </div>
                        <div
                          className="flex items-center gap-2 pointer-events-auto"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {option.price_modifier && option.price_modifier !== 0 ? (
                            <Badge variant="secondary" className="text-xs">
                              {option.price_modifier > 0 ? "+" : ""}$
                              {option.price_modifier.toFixed(2)}
                            </Badge>
                          ) : null}
                          {requiresQuantity && isChecked && (
                            <div className="w-20">
                              <Input
                                type="number"
                                inputMode="decimal"
                                min={component.min_quantity ?? 0}
                                value={optionQuantity ?? ""}
                                onChange={(event) => {
                                  const rawValue = event.target.value;
                                  const updatedQuantities = { ...existingOptionQuantities };
                                  if (rawValue === "") {
                                    delete updatedQuantities[optionValue];
                                  } else {
                                    const parsed = Number(rawValue);
                                    if (!Number.isNaN(parsed)) {
                                      const clamped = clampQuantityUtil(component, parsed);
                                      updatedQuantities[optionValue] = clamped;
                                    }
                                  }
                                  emitCheckboxResponse(selectedValues, updatedQuantities);
                                  // Also call onQuantityChange for backward compatibility
                                  onQuantityChange?.(
                                    checklist.id,
                                    optionQuantityKey,
                                    updatedQuantities[optionValue]
                                  );
                                }}
                                placeholder="Qty"
                                className="h-8 text-sm"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }

            const boolValue = Boolean(scalarValue);
            return (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={component.field_name}
                  checked={boolValue}
                  onCheckedChange={(checked) =>
                    emitResponseChange(component, Boolean(checked) as ChecklistScalarValue)
                  }
                />
                <Label htmlFor={component.field_name} className="font-normal">
                  {component.help_text || component.label}
                </Label>
              </div>
            );
          }
          case "number":
            return (
              <Input
                type="number"
                inputMode="decimal"
                value={scalarValue ?? ""}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === "") {
                    onResponseChange(component.field_name, { value: "" });
                    return;
                  }

                  const parsed = Number(value);
                  if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
                    return;
                  }

                  emitResponseChange(component, parsed as ChecklistScalarValue, quantityValue);
                }}
                placeholder={component.placeholder || ""}
              />
            );
          case "textarea":
            return (
              <Textarea
                value={(scalarValue as string) || ""}
                onChange={(event) =>
                  emitResponseChange(
                    component,
                    event.target.value as ChecklistScalarValue,
                    quantityValue
                  )
                }
                placeholder={component.placeholder || ""}
                rows={3}
              />
            );
          case "text":
          default:
            return (
              <Input
                value={(scalarValue as string) || ""}
                onChange={(event) =>
                  emitResponseChange(
                    component,
                    event.target.value as ChecklistScalarValue,
                    quantityValue
                  )
                }
                placeholder={component.placeholder || ""}
              />
            );
        }
      };

      return (
        <div key={component.id} className={containerClasses}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">
                  {component.label}
                  {component.is_required && <span className="ml-1 text-red-500">*</span>}
                </Label>
                {component.base_price && component.base_price > 0 && (
                  <Badge variant="outline" className="text-xs">
                    Base ${component.base_price.toFixed(2)}
                  </Badge>
                )}
              </div>
              {renderOptions()}
              {component.help_text && (
                <p className="text-xs text-muted-foreground">{component.help_text}</p>
              )}
              {hasError && validation?.error && (
                <p className="text-xs text-red-500">{validation.error}</p>
              )}
            </div>
            {componentRequiresQuantityUtil(component) &&
              component.component_type !== "checkbox" && (
                <div className="w-32 space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {component.pricing_unit_label || component.unit_label || "Quantity"}
                  </Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min={component.min_quantity ?? 0}
                    value={quantityValue ?? ""}
                    onChange={(event) => handleQuantityChange(component, event.target.value)}
                  />
                </div>
              )}
          </div>

          {component.children && component.children.length > 0 && (
            <div className="space-y-2">
              {component.children.map((child) => renderComponent(child, depth + 1))}
            </div>
          )}
        </div>
      );
    };

    const canConfirm = validationSummary.isValid;

    const handleConfirm = () => {
      if (!validationSummary.isValid) {
        toast({
          title: "Required Fields Missing",
          description:
            validationSummary.errors.length === 1
              ? validationSummary.errors[0]
              : `Please complete all required fields:\n${validationSummary.errors.join("\n")}`,
          variant: "destructive",
        });
        return;
      }

      onChecklistComplete?.(checklist.id);
      setActiveItemId(null);
    };

    useEffect(() => {
      if (activeItem && !isComponentVisible(activeItem)) {
        setActiveItemId(null);
      }
    }, [activeItem, isComponentVisible]);

    const renderItemCard = (component: ChecklistComponent, index: number) => {
      const componentType = getComponentType(component);
      const Icon = componentTypeIcon[componentType];
      const completed = isComponentCompleted(component);
      const isActive = activeItemId === component.id;

      return (
        <Card
          key={component.id || `${component.field_name}-${index}`}
          onClick={() => setActiveItemId(component.id ?? null)}
          className={`transition-all duration-200 hover:shadow-md cursor-pointer ${
            completed ? "border-green-500 bg-green-50/40" : "hover:border-primary/40"
          } ${isActive ? "ring-2 ring-primary" : ""}`}
        >
          <CardContent className="py-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-full text-base font-semibold ${
                    completed ? "bg-green-500 text-white" : "bg-primary/10 text-primary"
                  }`}
                >
                  {index + 1}
                </span>
                <div>
                  <div className="flex items-center gap-3">
                    <p className="text-lg font-medium text-foreground">{component.label}</p>
                    <Badge variant="outline" className="text-xs">
                      {componentTypeLabel[componentType]}
                    </Badge>
                  </div>
                  {component.help_text && (
                    <p className="text-sm text-muted-foreground mt-1">{component.help_text}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon className="h-5 w-5" />
                {completed ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <ArrowRight className="h-5 w-5" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      );
    };

    const activeItemType = activeItem ? getComponentType(activeItem) : undefined;
    const ActiveTaskIcon = activeItemType ? componentTypeIcon[activeItemType] : CheckCircle;
    const activeItemCompleted = activeItem ? isComponentCompleted(activeItem) : false;

    return (
      <div className="space-y-6">
        <Card className="border border-primary/20 bg-card">
          <CardContent className="py-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">{checklist.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {completedItemCount} of {visibleRootComponents.length} items completed
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Completion</p>
                  <p className="text-2xl font-semibold text-primary">{completionPercentage}%</p>
                </div>
                {activeItemId !== null && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (activeItem) {
                        saveItemDetails(activeItem.id);
                      }
                      setActiveItemId(null);
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                )}
              </div>
            </div>
            <Progress value={completionPercentage} className="mt-4" />
          </CardContent>
        </Card>

        {/* Wall-by-Wall Calculator - appears when enabled on checklist */}
        {checklist.wall_by_wall && (
          <Card className="border-2 border-blue-300 bg-blue-50/30">
            <CardContent className="py-6">
              <div className="flex items-center gap-3 mb-4">
                <Ruler className="h-6 w-6 text-blue-600" />
                <div>
                  <h4 className="text-lg font-semibold text-blue-900">
                    Wall & Floor Area Calculator
                  </h4>
                  <p className="text-sm text-blue-700">Calculate total area for walls and floor</p>
                </div>
              </div>
              <CalculationInput
                component={
                  {
                    id: checklist.id * -1, // Negative ID to avoid conflicts
                    checklist_id: checklist.id,
                    component_type: "number",
                    field_name: "_wall_calculation",
                    label: "Area Calculation",
                    is_required: false,
                    display_order: -1, // Display at top
                    calculation_formula:
                      "((wall1Length * wall1Height) + (wall2Length * wall2Height) + (wall3Length * wall3Height) + (wall4Length * wall4Height) + (wall5Length * wall5Height) + (wall6Length * wall6Height) + (wall7Length * wall7Height) + (wall8Length * wall8Height) + (wall9Length * wall9Height) + (wall10Length * wall10Height) + (includeFloor * floorLength * floorWidth)) * (1 + (wastagePercent / 100))",
                    calculation_inputs: JSON.stringify([
                      "numWalls",
                      "wall1Length",
                      "wall1Height",
                      "wall2Length",
                      "wall2Height",
                      "wall3Length",
                      "wall3Height",
                      "wall4Length",
                      "wall4Height",
                      "wall5Length",
                      "wall5Height",
                      "wall6Length",
                      "wall6Height",
                      "wall7Length",
                      "wall7Height",
                      "wall8Length",
                      "wall8Height",
                      "wall9Length",
                      "wall9Height",
                      "wall10Length",
                      "wall10Height",
                      "floorLength",
                      "floorWidth",
                      "includeFloor",
                      "wastagePercent",
                    ]),
                    calculation_display_unit: "m²",
                    calculation_decimal_places: 2,
                  } as any
                }
                value={responses["_wall_calculation"] || {}}
                onChange={(value) => {
                  onResponseChange("_wall_calculation", value);
                }}
              />
            </CardContent>
          </Card>
        )}

        {activeItem && activeItemId !== null ? (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => setActiveItemId(null)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Checklist
                </Button>
                <Badge variant="secondary" className="text-xs">
                  {componentTypeLabel[activeItemType ?? "simple"]}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  saveItemDetails(activeItem.id);
                  setActiveItemId(null);
                }}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
              >
                <ActiveTaskIcon className="h-5 w-5" />
                {activeItemCompleted ? (
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="h-4 w-4" /> Completed
                  </span>
                ) : (
                  <span>In progress</span>
                )}
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </div>

            <Card className="shadow-sm">
              <CardContent className="space-y-6 py-6">
                <div className="space-y-2">
                  <h4 className="text-xl font-semibold text-foreground">{activeItem.label}</h4>
                  {activeItem.help_text && (
                    <p className="text-sm text-muted-foreground">{activeItem.help_text}</p>
                  )}
                </div>
                <div className="space-y-4">{renderComponent(activeItem)}</div>

                {/* Add Details Section */}
                <div className="border-t pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setShowDetails((prev) => ({ ...prev, [activeItem.id]: !prev[activeItem.id] }))
                    }
                    className="mb-2 border-blue-300 text-blue-700 hover:bg-blue-50 hover:border-blue-400"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {showDetails[activeItem.id] ? "Hide Details" : "Add Details"}
                  </Button>

                  {showDetails[activeItem.id] && (
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <Label htmlFor={`notes-${activeItem.id}`}>Notes</Label>
                        <Textarea
                          id={`notes-${activeItem.id}`}
                          placeholder="Add any additional notes or details..."
                          value={itemNotes[activeItem.id] || ""}
                          onChange={(e) =>
                            setItemNotes((prev) => ({ ...prev, [activeItem.id]: e.target.value }))
                          }
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label>Images</Label>
                        <PhotoUpload
                          componentId={activeItem.id}
                          fieldName={`item-${activeItem.id}-images`}
                          value={itemImages[activeItem.id] || []}
                          onChange={(photos) => {
                            const photoArray = Array.isArray(photos) ? photos : [photos];
                            setItemImages((prev) => ({ ...prev, [activeItem.id]: photoArray }));
                          }}
                          allowMultiple={true}
                          maxFiles={10}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      saveItemDetails(activeItem.id);
                      setActiveItemId(null);
                    }}
                  >
                    Close Item
                  </Button>
                  <Button
                    onClick={() => {
                      saveItemDetails(activeItem.id);
                      goToNextItem();
                    }}
                    disabled={!activeItemCompleted}
                    size="sm"
                  >
                    Next Item
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-4">
            {visibleRootComponents.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No tasks available for this checklist.
                </CardContent>
              </Card>
            ) : (
              visibleRootComponents.map((component, index) => renderItemCard(component, index))
            )}
          </div>
        )}

        <div className="border-t pt-6">
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setActiveItemId(null);
                onCancel?.();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Handle legacy static checklists
  const { checklist, isCompleted, onComplete, onDelete } = props as LegacyQuoteChecklistProps;

  const handleComplete = () => {
    if (!isCompleted) {
      onComplete();
    }
  };

  const updateComponentValue = (fieldName: string, value: ChecklistResponseValue) => {
    setComponentValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Validate components when checklist has dynamic components
    if (checklist.components && checklist.components.length > 0) {
      const updatedValues = {
        ...componentValues,
        [fieldName]: value,
      };
      const validationResults = validateChecklistComponents(updatedValues, checklist.components);
      setValidationErrors(validationResults);
    }
  };

  const renderDynamicComponent = (component: ChecklistComponent) => {
    const value = componentValues[component.field_name] || "";
    // Support both new component_type and legacy type fields
    const componentType = component.component_type || component.type;
    const isRequired = component.is_required ?? component.required ?? false;
    // Handle both new ComponentOption[] format and legacy string[] format
    const optionsList =
      component.options?.map((option) =>
        typeof option === "string" ? option : option.option_label
      ) || [];

    const validationError = validationErrors[component.field_name];
    const hasError = validationError && !validationError.isValid;

    switch (componentType) {
      case "text":
        return (
          <div key={component.id} className="space-y-2">
            <Label htmlFor={component.field_name}>
              {component.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={component.field_name}
              placeholder={component.placeholder}
              value={value}
              onChange={(e) => updateComponentValue(component.field_name, e.target.value)}
              className={hasError ? "border-red-500 focus:border-red-500" : ""}
            />
            {hasError && <p className="text-sm text-red-500">{validationError.error}</p>}
          </div>
        );

      case "textarea":
        return (
          <div key={component.id} className="space-y-2">
            <Label htmlFor={component.field_name}>
              {component.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              id={component.field_name}
              placeholder={component.placeholder}
              value={value}
              onChange={(e) => updateComponentValue(component.field_name, e.target.value)}
              rows={3}
              className={hasError ? "border-red-500 focus:border-red-500" : ""}
            />
            {hasError && <p className="text-sm text-red-500">{validationError.error}</p>}
          </div>
        );

      case "select":
        return (
          <div key={component.id} className="space-y-2">
            <Label htmlFor={component.field_name}>
              {component.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={value}
              onValueChange={(val) => updateComponentValue(component.field_name, val)}
            >
              <SelectTrigger id={component.field_name}>
                <SelectValue placeholder={component.placeholder || "Choose an option..."} />
              </SelectTrigger>
              <SelectContent>
                {optionsList.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "radio":
        return (
          <div key={component.id} className="space-y-2">
            <Label>
              {component.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <RadioGroup
              value={value}
              onValueChange={(val) => updateComponentValue(component.field_name, val)}
              className="flex flex-wrap gap-4"
            >
              {optionsList.map((option) => (
                <div key={option} className="flex items-center space-x-2">
                  <RadioGroupItem value={option} id={`${component.field_name}-${option}`} />
                  <Label htmlFor={`${component.field_name}-${option}`} className="font-normal">
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case "checkbox": {
        const checkboxValues = Array.isArray(value) ? (value as string[]) : [];
        return (
          <div key={component.id} className="space-y-2">
            <Label>
              {component.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2">
              {optionsList.map((option) => (
                <div key={option} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${component.field_name}-${option}`}
                    checked={checkboxValues.includes(option)}
                    onCheckedChange={(checked) => {
                      const newValues = checked
                        ? [...checkboxValues, option]
                        : checkboxValues.filter((v: string) => v !== option);
                      updateComponentValue(component.field_name, newValues);
                    }}
                  />
                  <Label htmlFor={`${component.field_name}-${option}`} className="font-normal">
                    {option}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case "number":
        return (
          <div key={component.id} className="space-y-2">
            <Label htmlFor={component.field_name}>
              {component.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={component.field_name}
              type="number"
              inputMode="decimal"
              placeholder={component.placeholder}
              value={value}
              onChange={(e) => updateComponentValue(component.field_name, e.target.value)}
              className={hasError ? "border-red-500 focus:border-red-500" : ""}
            />
            {hasError && <p className="text-sm text-red-500">{validationError.error}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  const addCustomLine = () => {
    setCustomLines([...customLines, ""]);
  };

  const removeCustomLine = (index: number) => {
    setCustomLines(customLines.filter((_, i) => i !== index));
  };

  const updateCustomLine = (index: number, value: string) => {
    const newLines = [...customLines];
    newLines[index] = value;
    setCustomLines(newLines);
  };

  const renderChecklistContent = () => {
    // If checklist has dynamic components, render those instead of hardcoded types
    if (checklist.components && checklist.components.length > 0) {
      // Use proper validation system
      const validationResults = validateChecklistComponents(componentValues, checklist.components);
      const hasValidationErrors = !Object.values(validationResults).every(
        (result) => result.isValid
      );

      return (
        <div className="space-y-4">
          {checklist.components.map((component) => renderDynamicComponent(component))}
          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleComplete}
              disabled={hasValidationErrors}
              variant={isCompleted ? "default" : "outline"}
            >
              {isCompleted ? "Completed" : "Confirm"}
            </Button>
            <Button onClick={onDelete} variant="destructive" size="sm">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      );
    }

    // Fallback to original hardcoded checklist types for backwards compatibility
    switch (checklist.type) {
      case "simple":
        return (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={handleComplete} variant={isCompleted ? "default" : "outline"}>
                {isCompleted ? "Completed" : "Confirm"}
              </Button>
              <Button onClick={onDelete} variant="destructive" size="sm">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case "dropdown":
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor={`checklist-${checklist.id}`}>Select Option</Label>
              <Select value={selectedValue} onValueChange={setSelectedValue}>
                <SelectTrigger id={`checklist-${checklist.id}`}>
                  <SelectValue placeholder="Choose an option..." />
                </SelectTrigger>
                <SelectContent>
                  {checklist.options?.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleComplete}
                disabled={!selectedValue}
                variant={isCompleted ? "default" : "outline"}
              >
                {isCompleted ? "Completed" : "Confirm"}
              </Button>
              <Button onClick={onDelete} variant="destructive" size="sm">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case "yesno":
        return (
          <div className="space-y-4">
            <div>
              <Label>Select Option</Label>
              <Select value={selectedValue} onValueChange={setSelectedValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleComplete}
                disabled={!selectedValue}
                variant={isCompleted ? "default" : "outline"}
              >
                {isCompleted ? "Completed" : "Confirm"}
              </Button>
              <Button onClick={onDelete} variant="destructive" size="sm">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case "complex":
        return (
          <div className="space-y-4">
            <div>
              <Label>Waterproofing Options</Label>
              <Select value={selectedValue} onValueChange={setSelectedValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose waterproofing type..." />
                </SelectTrigger>
                <SelectContent>
                  {checklist.options?.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleComplete}
                disabled={!selectedValue}
                variant={isCompleted ? "default" : "outline"}
              >
                {isCompleted ? "Completed" : "Confirm"}
              </Button>
              <Button onClick={onDelete} variant="destructive" size="sm">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case "measurements":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="width">Width (mm)</Label>
                <Input
                  id="width"
                  type="number"
                  inputMode="decimal"
                  placeholder="e.g., 2400"
                  value={measurements.width}
                  onChange={(e) => setMeasurements({ ...measurements, width: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="height">Height (mm)</Label>
                <Input
                  id="height"
                  type="number"
                  inputMode="decimal"
                  placeholder="e.g., 2400"
                  value={measurements.height}
                  onChange={(e) => setMeasurements({ ...measurements, height: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Tile Size</Label>
              <Select value={selectedValue} onValueChange={setSelectedValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose tile size..." />
                </SelectTrigger>
                <SelectContent>
                  {checklist.tileOptions?.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}mm
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleComplete}
                disabled={!(measurements.width && measurements.height && selectedValue)}
                variant={isCompleted ? "default" : "outline"}
              >
                {isCompleted ? "Completed" : "Confirm"}
              </Button>
              <Button onClick={onDelete} variant="destructive" size="sm">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case "electrical":
        return (
          <div className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <Label>Power Points</Label>
                    <div className="flex items-center space-x-4 mt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPowerPoints(Math.max(1, powerPoints - 1))}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="text-lg font-semibold min-w-[2rem] text-center">
                        {powerPoints}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPowerPoints(Math.min(3, powerPoints + 1))}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <Label className="text-base font-semibold">Additional Options</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="xl"
                          checked={electricalOptions.xl}
                          onCheckedChange={(checked) =>
                            setElectricalOptions({ ...electricalOptions, xl: checked as boolean })
                          }
                        />
                        <Label htmlFor="xl">XL Option</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="downlights"
                          checked={electricalOptions.downlights}
                          onCheckedChange={(checked) =>
                            setElectricalOptions({
                              ...electricalOptions,
                              downlights: checked as boolean,
                            })
                          }
                        />
                        <Label htmlFor="downlights">Downlights</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="heatedTowelRail"
                          checked={electricalOptions.heatedTowelRail}
                          onCheckedChange={(checked) =>
                            setElectricalOptions({
                              ...electricalOptions,
                              heatedTowelRail: checked as boolean,
                            })
                          }
                        />
                        <Label htmlFor="heatedTowelRail">Heated Towel Rail</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="ledCabinet"
                          checked={electricalOptions.ledCabinet}
                          onCheckedChange={(checked) =>
                            setElectricalOptions({
                              ...electricalOptions,
                              ledCabinet: checked as boolean,
                            })
                          }
                        />
                        <Label htmlFor="ledCabinet">LED Cabinet</Label>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="flex gap-2">
              <Button onClick={handleComplete} variant={isCompleted ? "default" : "outline"}>
                {isCompleted ? "Completed" : "Confirm"}
              </Button>
              <Button onClick={onDelete} variant="destructive" size="sm">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case "vanity":
        return (
          <div className="space-y-4">
            <div>
              <Label>Vanity Type</Label>
              <Select value={vanityType} onValueChange={setVanityType}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose vanity type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wall-hung">Wall Hung</SelectItem>
                  <SelectItem value="floor-mount">Floor Mount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vanity Size</Label>
              <Select value={vanitySize} onValueChange={setVanitySize}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose size..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="600">600mm</SelectItem>
                  <SelectItem value="750">750mm</SelectItem>
                  <SelectItem value="900">900mm</SelectItem>
                  <SelectItem value="1200">1200mm</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {vanitySize === "custom" && (
              <div>
                <Label htmlFor="custom-notes">Custom Size Notes</Label>
                <Textarea
                  id="custom-notes"
                  placeholder="Enter custom measurements and notes..."
                  value={customNotes}
                  onChange={(e) => setCustomNotes(e.target.value)}
                />
              </div>
            )}
            <div className="flex gap-2">
              <Button
                onClick={handleComplete}
                disabled={!(vanityType && vanitySize) || (vanitySize === "custom" && !customNotes)}
                variant={isCompleted ? "default" : "outline"}
              >
                {isCompleted ? "Completed" : "Confirm"}
              </Button>
              <Button onClick={onDelete} variant="destructive" size="sm">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case "supply":
        return (
          <div className="space-y-4">
            <div>
              <Label>Supply Option</Label>
              <Select value={selectedValue} onValueChange={setSelectedValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Who supplies..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client-supply">Client Supply</SelectItem>
                  <SelectItem value="we-supply">We Supply</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleComplete}
                disabled={!selectedValue}
                variant={isCompleted ? "default" : "outline"}
              >
                {isCompleted ? "Completed" : "Confirm"}
              </Button>
              <Button onClick={onDelete} variant="destructive" size="sm">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case "screen":
        return (
          <div className="space-y-4">
            <div>
              <Label>Shower Screen Size</Label>
              <Select value={selectedValue} onValueChange={setSelectedValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose screen size..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="900x900">900 x 900mm</SelectItem>
                  <SelectItem value="900x1200">900 x 1200mm</SelectItem>
                  <SelectItem value="custom">Custom Size</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedValue === "custom" && (
              <div>
                <Label htmlFor="screen-notes">Custom Size Notes</Label>
                <Textarea
                  id="screen-notes"
                  placeholder="Enter custom screen measurements..."
                  value={customNotes}
                  onChange={(e) => setCustomNotes(e.target.value)}
                />
              </div>
            )}
            <div className="flex gap-2">
              <Button
                onClick={handleComplete}
                disabled={!selectedValue || (selectedValue === "custom" && !customNotes)}
                variant={isCompleted ? "default" : "outline"}
              >
                {isCompleted ? "Completed" : "Confirm"}
              </Button>
              <Button onClick={onDelete} variant="destructive" size="sm">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case "custom":
        return (
          <div className="space-y-4">
            <Label className="text-base font-semibold">Custom Items</Label>
            {customLines.map((line, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Input
                  placeholder="Enter custom item..."
                  value={line}
                  onChange={(e) => updateCustomLine(index, e.target.value)}
                  className="flex-1"
                />
                {customLines.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeCustomLine(index)}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            <div className="flex items-center space-x-2">
              <Button type="button" variant="outline" size="sm" onClick={addCustomLine}>
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
              <Button onClick={handleComplete} variant={isCompleted ? "default" : "outline"}>
                {isCompleted ? "Completed" : "Confirm"}
              </Button>
              <Button onClick={onDelete} variant="destructive" size="sm">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {renderChecklistContent()}
      <div className="pt-4 border-t">
        <Label htmlFor={`notes-${checklist.id}`} className="text-sm font-medium">
          Optional Notes
        </Label>
        <Textarea
          id={`notes-${checklist.id}`}
          placeholder="Add any additional notes for this checklist..."
          value={optionalNotes}
          onChange={(e) => setOptionalNotes(e.target.value)}
          className="mt-2"
          rows={3}
        />
      </div>
    </div>
  );
};

export default QuoteChecklist;
